import { useQueries } from '@tanstack/react-query';

import {
  Grouping,
  TokenUsageWithBreakdown,
} from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { Granularity } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { useTelemetryService } from '../../../telemetry/hooks';

/**
 * All H2OGPTe groupings that we prefetch
 */
const ALL_GROUPINGS = [Grouping.WORKSPACE, Grouping.USER, Grouping.MODEL] as const;

/**
 * Result type for the multi-dimensional token prefetch hook
 */
export interface TokenPrefetchResult {
  /** Data for the currently selected grouping */
  usageData: TokenUsageWithBreakdown[] | undefined;
  /** Whether the current grouping data is loading */
  isLoading: boolean;
  /** Error if any occurred */
  error: Error | undefined;
  /** Refetch function for current data */
  refetch: () => void;
  /** Data grouped by WORKSPACE (for workspace filter options) */
  workspaceData: TokenUsageWithBreakdown[] | undefined;
  /** Data grouped by USER (for user filter options) */
  userData: TokenUsageWithBreakdown[] | undefined;
  /** Data grouped by MODEL (for model filter options) */
  modelData: TokenUsageWithBreakdown[] | undefined;
  /** Whether all groupings have finished loading */
  isAllLoaded: boolean;
}

/**
 * Custom hook that prefetches H2OGPTe token usage data for all 3 groupings
 *
 * Fetches WORKSPACE, USER, and MODEL groupings in parallel to:
 * 1. Provide instant switching between grouping views
 * 2. Extract filter options for workspace/user/model filters
 *
 * @param selectedModels Array of model filter strings
 * @param selectedWorkspaces Array of workspace filter strings (UUIDs)
 * @param selectedUsers Array of user filter strings (UUIDs)
 * @param currentTimeRange Currently selected time range and granularity
 * @param currentGroupBy Currently selected grouping option
 * @returns Object containing usage data for all groupings and current selection state
 */
export function useMultiDimensionalTokenPrefetch(
  selectedModels: string[],
  selectedWorkspaces: string[],
  selectedUsers: string[],
  currentTimeRange: { startTime: Date; endTime: Date; granularity: Granularity },
  currentGroupBy: Grouping
): TokenPrefetchResult {
  const TelemetryService = useTelemetryService();

  // Cache duration: 30 minutes
  const cacheDurationMs = 1000 * 60 * 30;

  // Fetch all 3 groupings in parallel
  const queries = useQueries({
    queries: ALL_GROUPINGS.map((groupBy) => ({
      queryKey: [
        'tokenUsageData',
        {
          startTime: currentTimeRange.startTime.toISOString(),
          endTime: currentTimeRange.endTime.toISOString(),
          granularity: currentTimeRange.granularity,
          groupBy,
          modelsKey: selectedModels.length > 0 ? [...selectedModels].sort().join(',') : 'none',
          workspacesKey: selectedWorkspaces.length > 0 ? [...selectedWorkspaces].sort().join(',') : 'none',
          usersKey: selectedUsers.length > 0 ? [...selectedUsers].sort().join(',') : 'none',
        },
      ],
      queryFn: async (): Promise<TokenUsageWithBreakdown[]> => {
        const startTimeISO = currentTimeRange.startTime.toISOString();
        const endTimeISO = currentTimeRange.endTime.toISOString();

        // Build combined filter string from all filter types
        let filterString: string | undefined = undefined;
        const filterParts: string[] = [];

        // Model filter
        if (selectedModels.length > 0) {
          const modelFilter = selectedModels.map((model) => `model = '${model}'`).join(' OR ');
          filterParts.push(`(${modelFilter})`);
        }

        // Workspace filter
        if (selectedWorkspaces.length > 0) {
          const workspaceFilter = selectedWorkspaces.map((ws) => `workspace = '${ws}'`).join(' OR ');
          filterParts.push(`(${workspaceFilter})`);
        }

        // User filter
        if (selectedUsers.length > 0) {
          const userFilter = selectedUsers.map((user) => `user = '${user}'`).join(' OR ');
          filterParts.push(`(${userFilter})`);
        }

        // Combine all filter parts with AND
        if (filterParts.length > 0) {
          filterString = filterParts.join(' AND ');
        }

        const response = await TelemetryService.calculateTokenUsage({
          startTime: startTimeISO,
          endTime: endTimeISO,
          granularity: currentTimeRange.granularity,
          groupBy,
          ...(filterString && { filter: filterString }),
        });

        return response.usage || [];
      },
      staleTime: cacheDurationMs,
      cacheTime: cacheDurationMs,
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onError: (error: Error) => {
        console.error('[useMultiDimensionalTokenPrefetch] Error:', error, { groupBy });
      },
    })),
  });

  // Find the index of the current groupBy in ALL_GROUPINGS
  const currentIndex = (ALL_GROUPINGS as readonly Grouping[]).indexOf(currentGroupBy);
  const currentQuery = currentIndex >= 0 ? queries[currentIndex] : queries[0];

  // Get data for each grouping
  const workspaceQuery = queries[ALL_GROUPINGS.indexOf(Grouping.WORKSPACE)];
  const userQuery = queries[ALL_GROUPINGS.indexOf(Grouping.USER)];
  const modelQuery = queries[ALL_GROUPINGS.indexOf(Grouping.MODEL)];

  // Check if all queries are done loading
  const isAllLoaded = queries.every((q) => !q.isLoading);

  return {
    usageData: currentQuery?.data,
    isLoading: currentQuery?.isLoading ?? true,
    error: (currentQuery?.error as Error) || undefined,
    refetch: () => currentQuery?.refetch(),
    workspaceData: workspaceQuery?.data,
    userData: userQuery?.data,
    modelData: modelQuery?.data,
    isAllLoaded,
  };
}
